import { supabase } from './supabase';

export function toIsoAtAest3(y, m, d) {
  const year = Number(y);
  const monIdx = Number(m) - 1;
  const day = Number(d);
  return new Date(Date.UTC(year, monIdx, day, -7)).toISOString();
}

export function computeBootcampEndIso(startDateStr, stutterDays, lessonCount) {
  const stutterInt = parseInt(stutterDays, 10);
  if (!startDateStr || Number.isNaN(stutterInt) || stutterInt < 1 || !lessonCount) return '';
  const [yy, mm, dd] = startDateStr.split('-').map((v) => parseInt(v, 10));
  if (!yy || !mm || !dd) return '';
  const startUtc = new Date(Date.UTC(yy, mm - 1, dd));
  const endUtc = new Date(startUtc.getTime() + stutterInt * lessonCount * 24 * 60 * 60 * 1000);
  return toIsoAtAest3(endUtc.getUTCFullYear(), endUtc.getUTCMonth() + 1, endUtc.getUTCDate());
}

export function formatBootcampDate(date) {
  const d = new Date(date);
  if (!d || Number.isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export async function getServerTime() {
  const { data: serverTime } = await supabase.rpc('get_server_time');
  return new Date(serverTime);
}

export async function fetchActiveCoursesWithLessons() {
  const { data: courses } = await supabase.from('courses').select('id, name, active').eq('active', true);
  if (!courses?.length) return [];
  const { data: lessons } = await supabase.from('lessons').select('course_id');
  if (!lessons) return [];
  const lessonCountMap = {};
  lessons.forEach((l) => {
    lessonCountMap[l.course_id] = (lessonCountMap[l.course_id] || 0) + 1;
  });
  return courses.filter((c) => lessonCountMap[c.id] > 0);
}

export async function fetchLessonCountForCourse(courseId) {
  const { data: lessonRows } = await supabase
    .from('lessons')
    .select('lesson_id, lesson_type')
    .eq('course_id', courseId);
  const filtered = (lessonRows || []).filter((l) => l.lesson_type !== 'onboarding');
  return filtered.length || (lessonRows || []).length;
}

export async function refreshTeamBootcamps(teamId) {
  const currentTime = await getServerTime();
  const nowIso = currentTime.toISOString();
  const { data: activeData } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name), teams(name)')
    .eq('team_id', teamId)
    .lte('start_date', nowIso)
    .gt('end_date', nowIso);
  const { data: upcomingData } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name), teams(name)')
    .eq('team_id', teamId)
    .gt('start_date', nowIso);
  return {
    activeBootcamp: activeData?.[0] || null,
    upcomingBootcamp: upcomingData?.[0] || null,
  };
}

export async function loadClientTeamsWithBootcamps(clientId) {
  const { data: teamsData, error: teamsErr } = await supabase
    .from('teams')
    .select('id, name, region_id, regions(name)')
    .eq('client_id', clientId)
    .eq('active', true)
    .order('name');
  if (teamsErr) throw teamsErr;

  const teams = teamsData || [];
  const teamIds = teams.map((t) => t.id);
  if (teamIds.length === 0) return [];

  const { data: teamUsers } = await supabase
    .from('team_users')
    .select('team_id, user_id')
    .in('team_id', teamIds);
  const userCountByTeam = {};
  (teamUsers || []).forEach((tu) => {
    userCountByTeam[tu.team_id] = (userCountByTeam[tu.team_id] || 0) + 1;
  });

  const currentTime = await getServerTime();
  const nowIso = currentTime.toISOString();
  const { data: activeBootcamps } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name)')
    .in('team_id', teamIds)
    .lte('start_date', nowIso)
    .gt('end_date', nowIso);
  const { data: upcomingBootcamps } = await supabase
    .from('bootcamps')
    .select('id, start_date, end_date, stutter_time, team_id, course_id, courses(name)')
    .in('team_id', teamIds)
    .gt('start_date', nowIso);

  const activeByTeam = new Map((activeBootcamps || []).map((b) => [b.team_id, b]));
  const upcomingByTeam = new Map((upcomingBootcamps || []).map((b) => [b.team_id, b]));

  return teams.map((t) => ({
    ...t,
    userCount: userCountByTeam[t.id] || 0,
    activeBootcamp: activeByTeam.get(t.id) || null,
    upcomingBootcamp: upcomingByTeam.get(t.id) || null,
  }));
}

export async function createBootcamp({ teamId, courseId, startDate, stutterDays, endDateIso }) {
  const stutterInt = parseInt(stutterDays, 10);
  const stutterStr = `${stutterInt} days`;
  const [yy, mm, dd] = (startDate || '').split('-').map((v) => parseInt(v, 10));
  const startUtcIso = yy && mm && dd ? toIsoAtAest3(yy, mm, dd) : new Date(startDate).toISOString();
  return supabase.from('bootcamps').insert({
    start_date: startUtcIso,
    stutter_time: stutterStr,
    course_id: courseId,
    team_id: teamId,
    end_date: endDateIso,
  });
}
